import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { formatIDR, formatDate, formatNumber } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import OrderDialog from "@/components/OrderDialog";
import PrintDoc from "@/components/PrintDoc";
import { Plus, Search, MoreVertical, Printer, Receipt, Pencil, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [print, setPrint] = useState(null); // {order, mode}
  const [del, setDel] = useState(null);

  const load = useCallback(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (location.state?.openNew) { setEditOrder(null); setPrefill(null); setDialog(true); }
    if (location.state?.prefill) { setEditOrder(null); setPrefill(location.state.prefill); setDialog(true); }
    if (location.state) window.history.replaceState({}, "");
  }, [location.state]);

  const filtered = orders.filter((o) =>
    [o.order_id, o.customer_name, o.company, o.design_name].join(" ").toLowerCase().includes(q.toLowerCase())
  );

  const doDelete = async () => {
    try { await api.delete(`/orders/${del.id}`); toast.success("Order dihapus"); load(); }
    catch (e) { toast.error(apiErr(e)); }
    setDel(null);
  };

  const openNew = () => { setEditOrder(null); setPrefill(null); setDialog(true); };
  const openEdit = (o) => { setEditOrder(o); setPrefill(null); setDialog(true); };
  const wa = (o) => {
    const msg = encodeURIComponent(`Halo ${o.customer_name}, update pesanan bordir Anda ${o.order_id} (${o.design_name}). Status: ${o.status}. Terima kasih - BordirKom`);
    window.open(`https://wa.me/${o.phone}?text=${msg}`, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Order & SPK</h1>
          <p className="text-sm text-slate-500">Kelola pesanan masuk, pembayaran, dan cetak SPK / nota.</p>
        </div>
        <Button data-testid="btn-create-order" className="bg-blue-600 hover:bg-blue-700" onClick={openNew}>
          <Plus className="mr-2 h-4 w-4" /> Terima Pesanan Baru
        </Button>
      </div>

      <Card className="border-slate-200/80 shadow-sm">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="input-order-search" className="pl-9" placeholder="Cari order ID, pelanggan, desain..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="table-orders-list">
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead>Order ID</TableHead>
                <TableHead>Pelanggan</TableHead>
                <TableHead>Desain</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Pembayaran</TableHead>
                <TableHead>Produksi</TableHead>
                <TableHead>Target</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.order_id}`} className="hover:bg-slate-50/60">
                  <TableCell className="font-mono text-xs text-blue-600">{o.order_id}</TableCell>
                  <TableCell>
                    <p className="font-semibold text-slate-800">{o.customer_name}</p>
                    <p className="text-xs text-slate-500">{o.company}</p>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{o.design_name}<div className="text-xs text-slate-400">{formatNumber(o.stitch_count)} stitch · {o.qty} pcs</div></TableCell>
                  <TableCell className="font-semibold text-slate-800">{formatIDR(o.total)}</TableCell>
                  <TableCell><StatusBadge value={o.payment_status} kind="payment" /></TableCell>
                  <TableCell><StatusBadge value={o.status} /></TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDate(o.target_date, true)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button data-testid={`order-actions-${o.order_id}`} variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`btn-print-spk-${o.order_id}`} onClick={() => setPrint({ order: o, mode: "spk" })}><Printer className="mr-2 h-4 w-4" /> Cetak SPK</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`btn-print-nota-${o.order_id}`} onClick={() => setPrint({ order: o, mode: "nota" })}><Receipt className="mr-2 h-4 w-4" /> Cetak Nota</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => wa(o)}><MessageCircle className="mr-2 h-4 w-4" /> Kirim WhatsApp</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(o)}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                        <DropdownMenuItem className="text-rose-600" onClick={() => setDel(o)}><Trash2 className="mr-2 h-4 w-4" /> Hapus</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">Tidak ada order ditemukan.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <OrderDialog open={dialog} onOpenChange={setDialog} order={editOrder} prefill={prefill} onSaved={load} />
      <PrintDoc open={!!print} onOpenChange={(v) => !v && setPrint(null)} order={print?.order} mode={print?.mode} />

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus order {del?.order_id}?</AlertDialogTitle>
            <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-delete-order" className="bg-rose-600 hover:bg-rose-700" onClick={doDelete}>Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
